import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  Truck, 
  ShieldCheck, 
  Building2, 
  FileText,
  Target,
  Package,
  Factory,
  Layers,
  TestTube,
  HardHat,
  Pill,
  Home as HomeIcon
} from 'lucide-react';

const HomePage = () => {
  const products = [
    {
      name: 'Calcite Powder',
      description: 'High-purity calcium carbonate for industrial applications. Available in various mesh sizes for paint, plastic, and rubber industries.',
      link: '/products/calcite-powder'
    },
    {
      name: 'Bentonite',
      description: 'Premium quality sodium and calcium bentonite for drilling, foundry, and construction applications.',
      link: '/products/bentonite'
    },
    {
      name: 'Precipitated Calcium Carbonate (PCC)',
      description: 'Ultra-fine precipitated calcium carbonate for paper, paint, and pharmaceutical industries.',
      link: '/products/pcc'
    },
    {
      name: 'Tricalcium Phosphate (TCP)',
      description: 'Food and pharmaceutical grade TCP for nutritional supplements and animal feed applications.',
      link: '/products/tcp'
    },
    {
      name: 'Dicalcium Phosphate (DCP)',
      description: 'High-quality DCP for animal nutrition and pharmaceutical formulations.',
      link: '/products/dcp'
    },
    {
      name: 'Starch',
      description: 'Industrial-grade modified and native starch for paper, textile, and food industries.',
      link: '/products/starch'
    }
  ];

  const whyChooseUs = [
    { icon: Building2, title: 'Structured Vendor Network', description: 'Partnered with verified manufacturers across India' },
    { icon: ShieldCheck, title: 'Quality Assured Procurement', description: 'Batch-level testing and quality certification' },
    { icon: Package, title: 'Bulk Order Capability', description: 'Handle large-scale industrial requirements' },
    { icon: Truck, title: 'Reliable Dispatch Planning', description: 'Pan-India logistics and timely delivery' },
    { icon: FileText, title: 'Competitive Commercial Terms', description: 'Transparent pricing and flexible payment options' }
  ];

  const industries = [
    { icon: Target, name: 'Paint & Coatings' },
    { icon: Layers, name: 'Plastic & Polymer' },
    { icon: Factory, name: 'Rubber' },
    { icon: FileText, name: 'Paper' },
    { icon: HardHat, name: 'Construction' },
    { icon: TestTube, name: 'Animal Feed' },
    { icon: Pill, name: 'Pharmaceutical' },
    { icon: HomeIcon, name: 'Textiles' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.pexels.com/photos/10666081/pexels-photo-10666081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
            Industrial Chemicals & Minerals Supply Partner
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Reliable bulk supply of industrial minerals and chemical raw materials across India
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold">
                REQUEST A QUOTE
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="bg-white text-slate-900 hover:bg-gray-100 px-8 py-6 text-lg font-semibold border-2">
                EXPLORE PRODUCTS
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-8 border-b">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-2">Private Limited</h3>
              <p className="text-gray-600 uppercase text-sm font-semibold">Company Status</p>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-2">05AAHCV3224B2ZT</h3>
              <p className="text-gray-600 uppercase text-sm font-semibold">GSTIN</p>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-2">Pan-India</h3>
              <p className="text-gray-600 uppercase text-sm font-semibold">Supply Network</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Products</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive range of industrial chemicals and minerals for diverse applications
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-600">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{product.description}</p>
                  <Link to={product.link}>
                    <Button variant="outline" className="w-full font-semibold">
                      View Details →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Voltron Calcium</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your trusted partner for industrial chemical procurement
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center p-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Supplying critical raw materials across major industrial sectors
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="text-center p-6 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg">{industry.name}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Partner With Us?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Get in touch for bulk inquiries, technical specifications, or pricing information
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
              CONTACT US NOW
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
